// The flow engine in shadow (7.2 M3), in words: a run's state, a letter
// decision's outcome and reason, a step's result, why a run ended, a
// decision's checks in short, a run's move on one line — and the per-step
// numbers the flow page puts beside each step. A code these tables do not know
// is shown as it came: a new reason on the API must stay visible.
//
// Pure functions: tests/admin/test_flows_shadow_model.py runs them on Node.

import type {
  FlowDecisionCheck,
  FlowMode,
  FlowModeCandidate,
  FlowRunEventRow,
  FlowShadowStats,
  FlowShadowStepStats,
} from "@/lib/api";
import { labelOf, statusLook } from "@/components/emc/status";
import type { Tone } from "@/components/emc/status";

function counted(value: number): string {
  return value.toLocaleString("hu-HU");
}

// --- a run's state ---------------------------------------------------------------

export interface RunStateLook {
  label: string;
  tone: Tone;
}

const RUN_STATE: Record<string, RunStateLook> = {
  pending: { label: "felvéve", tone: "scheduled" },
  waiting: { label: "várakozik", tone: "scheduled" },
  running: { label: "lép", tone: "running" },
  completed: { label: "végigment", tone: "good" },
  exited: { label: "kilépett", tone: "muted" },
  rejected: { label: "nem lépett be", tone: "muted" },
  failed: { label: "hiba", tone: "danger" },
  unknown: { label: "bizonytalan", tone: "warn" },
};

/** The order the states are listed in: the way a run goes. */
export const RUN_STATES = Object.keys(RUN_STATE);

export function runStateLook(state: string): RunStateLook {
  return RUN_STATE[state] ?? { label: state, tone: "muted" };
}

export function runStateText(state: string | null | undefined): string {
  return state ? runStateLook(state).label : "—";
}

/** The runs by state, in the way a run goes, the ones the table does not
 * know after them; states with no run are left out. */
export function runsByState(byState: Record<string, number>): { state: string; label: string; tone: Tone; count: number }[] {
  const known = RUN_STATES.filter((state) => (byState[state] ?? 0) > 0);
  const other = Object.keys(byState)
    .filter((state) => !(state in RUN_STATE) && (byState[state] ?? 0) > 0)
    .sort();
  return [...known, ...other].map((state) => ({ state, ...runStateLook(state), count: byState[state] ?? 0 }));
}

// --- a letter decision -----------------------------------------------------------

const OUTCOME: Record<string, string> = {
  send: "küldené",
  skip: "kihagyná",
  held_manual: "kézi jóváhagyásra vár",
};

export function outcomeText(outcome: string): string {
  return OUTCOME[outcome] ?? outcome;
}

export function outcomeTone(outcome: string): Tone {
  if (outcome === "send") return "good";
  if (outcome === "held_manual") return "warn";
  return "muted";
}

const NOT_MAILABLE: Record<string, string> = {
  unsubscribed: "leiratkozott",
  never_subscribed: "sosem iratkozott fel",
  pending: "megerősítésre vár",
  absent: "nincs már a Klaviyo-fiókban",
  no_contact: "nincs kontakt",
  unknown: "ismeretlen ok",
};

/** Why an address may not be mailed: "leiratkozott", "kizárva: spam-panasz". */
export function notMailableText(why: string): string {
  if (why.startsWith("suppressed:")) {
    return `kizárva: ${labelOf("suppressionReason", why.slice("suppressed:".length))}`;
  }
  return NOT_MAILABLE[why] ?? why;
}

/** "a flow a Klaviyóban nem élő (vázlat)". */
function flowStatusText(status: string): string {
  return `a flow a Klaviyóban nem élő (${statusLook("flowSource", status).label})`;
}

/** A decision's reason in Hungarian: "küldené", "nem küldhető: leiratkozott",
 * "smart sending: 16 órán belül kapott levelet". An unknown code stays as it is. */
export function decisionReasonText(reason: string): string {
  if (reason === "send") return "küldené";
  if (reason === "held_manual") return "kézi jóváhagyásra vár";
  if (reason === "skip:no_contact") return "nincs kontakt";
  if (reason.startsWith("skip:not_mailable:")) {
    return `nem küldhető: ${notMailableText(reason.slice("skip:not_mailable:".length))}`;
  }
  if (reason === "skip:smart_sending") return "smart sending: 16 órán belül kapott levelet";
  if (reason === "skip:message_filter") return "a levél saját szűrője";
  if (reason.startsWith("skip:flow_status:")) return flowStatusText(reason.slice("skip:flow_status:".length));
  return reason;
}

// --- a step's result -------------------------------------------------------------

/** What a run did at a step: "várakozás", "Igen", "vázlat, átlépve",
 * "már döntött: küldené", or a letter decision's reason. */
export function stepResultText(result: string): string {
  if (result === "wait") return "várakozás";
  if (result === "yes") return "Igen";
  if (result === "no") return "Nem";
  if (result === "passed:draft") return "vázlat, átlépve";
  if (result === "exited:flow_filter") return "a flow-szűrő kiléptette";
  if (result.startsWith("decided_before:")) {
    return `már döntött: ${decisionReasonText(result.slice("decided_before:".length))}`;
  }
  return decisionReasonText(result);
}

// --- why a run ended -------------------------------------------------------------

const RUN_OUTCOME: Record<string, string> = {
  end: "végigment",
  flow_filter: "a flow-szűrő kiléptette",
  trigger_filter: "az indító-szűrő nem engedte",
  reentry: "újrabelépési ablakon belül",
  already_entered: "lista/szegmens: már volt bent",
  step_removed: "a lépés eltűnt az új verzióból",
};

export function runOutcomeText(reason: string | null | undefined): string {
  if (!reason) return "—";
  if (reason.startsWith("flow_status:")) return flowStatusText(reason.slice("flow_status:".length));
  return RUN_OUTCOME[reason] ?? reason;
}

// --- a decision's checks, in short -----------------------------------------------

export interface CheckLook {
  text: string;
  /** True: it let the letter (or the run) through; false: it stopped it;
   * null: a fact, neither. */
  ok: boolean | null;
}

function truth(value: unknown): string {
  return value ? "igaz" : "nem igaz";
}

/** One check of a decision or an entry, on one line. */
export function checkLook(check: FlowDecisionCheck): CheckLook {
  const holds = typeof check.holds === "boolean" ? check.holds : null;
  switch (check.check) {
    case "status": {
      const status = String(check.status ?? "");
      const variant = typeof check.variant === "string" ? check.variant : null;
      const which = variant === null ? "" : variant === "main" ? " · fő levél" : ` · változat #${variant}`;
      return { text: `lépés: ${statusLook("flowSource", status).label}${which}`, ok: status === "live" };
    }
    case "manual":
      return { text: "kézi jóváhagyás kell (a Klaviyóban kézi)", ok: false };
    case "flow_status":
      return { text: flowStatusText(String(check.status ?? "")), ok: false };
    case "contact":
      return check.contact_id === null || check.contact_id === undefined
        ? { text: "nincs kontakt", ok: false }
        : { text: `kontakt: ${String(check.contact_id)}`, ok: true };
    case "mailable":
      return holds
        ? { text: "küldhető", ok: true }
        : { text: `nem küldhető: ${notMailableText(String(check.why ?? "unknown"))}`, ok: false };
    case "smart_sending": {
      const hours = typeof check.hours === "number" ? check.hours : 16;
      return check.received
        ? { text: `smart sending: kapott levelet az utolsó ${hours} órában`, ok: false }
        : { text: `smart sending: nem kapott levelet az utolsó ${hours} órában`, ok: true };
    }
    case "message_filter":
      return { text: `a levél szűrője: ${truth(holds)}`, ok: holds };
    case "trigger_filter":
      return { text: `indító-szűrő: ${truth(holds)}`, ok: holds };
    case "flow_filter":
      return { text: `flow-szűrő: ${truth(holds)}`, ok: holds };
    case "reentry": {
      const runs = Array.isArray(check.runs) ? check.runs.length : 0;
      return holds
        ? { text: "újrabelépés: nem volt bent az ablakban", ok: true }
        : { text: `újrabelépés: ${counted(runs)} korábbi futás az ablakban`, ok: false };
    }
    case "once_per_profile":
      return holds ? { text: "először lép be", ok: true } : { text: "már volt bent", ok: false };
    default:
      return { text: holds === null ? check.check : `${check.check}: ${truth(holds)}`, ok: holds };
  }
}

// --- a run's move, on one line ---------------------------------------------------

const MOVE_REASON: Record<string, string> = {
  lease_expired: "a motor zárolása lejárt, visszaadva",
};

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** What a move of a run says, on one line: the error, what it waits for, why
 * it ended, how many steps it took and what the letters decided. */
export function eventSummary(event: FlowRunEventRow): string {
  const detail = event.detail ?? {};
  const parts: string[] = [];
  if (typeof detail.error === "string") parts.push(`hiba: ${detail.error.slice(0, 160)}`);
  if (Array.isArray(detail.blocked) && detail.blocked.length > 0) {
    const why = detail.blocked.map((each) => {
      const block = record(each);
      return String(block?.text ?? block?.code ?? "?");
    });
    parts.push(`adatra vár: ${why.join("; ")}`);
  }
  if (typeof detail.reason === "string") {
    parts.push(MOVE_REASON[detail.reason] ?? runOutcomeText(detail.reason));
  }
  if (Array.isArray(detail.steps) && detail.steps.length > 0) {
    parts.push(`${counted(detail.steps.length)} lépés`);
  }
  if (Array.isArray(detail.decisions) && detail.decisions.length > 0) {
    const reasons = detail.decisions.map((each) => decisionReasonText(String(record(each)?.reason ?? "?")));
    parts.push(`levél: ${reasons.join(", ")}`);
  }
  if (event.to === "running" && typeof detail.owner === "string") parts.push("a motor felvette");
  if (detail.partial === true) parts.push("hiányos adatból");
  return parts.length > 0 ? parts.join(" · ") : event.action;
}

// --- the per-step numbers --------------------------------------------------------

/** The stats by step id, for the outline to look a step up in. */
export function statsByStep(stats: FlowShadowStats): Record<string, FlowShadowStepStats> {
  return stats.steps ?? {};
}

function total(counts: Record<string, number> | undefined): number {
  return Object.values(counts ?? {}).reduce((sum, value) => sum + value, 0);
}

/** One step's numbers on one line: "123 futás · küldené 80 · kihagyná 40",
 * "50 futás · Igen 30 · Nem 20". A part that is zero is left out; the runs
 * count is always there. */
export function stepSummary(step: FlowShadowStepStats | undefined): string {
  if (step === undefined) return "0 futás";
  const parts = [`${counted(step.reached)} futás`];
  for (const outcome of ["send", "skip", "held_manual"]) {
    const value = total(step.decisions[outcome]);
    if (value > 0) parts.push(`${outcomeText(outcome)} ${counted(value)}`);
  }
  const results: [string, string][] = [
    ["yes", "Igen"],
    ["no", "Nem"],
    ["passed:draft", "átlépve"],
    ["exited:flow_filter", "kilépett"],
  ];
  for (const [result, label] of results) {
    const value = step.results[result] ?? 0;
    if (value > 0) parts.push(`${label} ${counted(value)}`);
  }
  // A multi-branch split's ways, by the branch's id (7.4 M5).
  for (const [result, value] of Object.entries(step.results).sort()) {
    if (result.startsWith("branch:") && value > 0) parts.push(`ág #${result.slice(7)} ${counted(value)}`);
  }
  return parts.join(" · ");
}

/** The reasons behind a step's decisions, one line each, most first:
 * "kihagyná — nem küldhető: leiratkozott: 30". For the badge's hover. */
export function stepBreakdown(step: FlowShadowStepStats | undefined): string[] {
  if (step === undefined) return [];
  const lines: { text: string; value: number }[] = [];
  for (const [outcome, reasons] of Object.entries(step.decisions)) {
    for (const [reason, value] of Object.entries(reasons)) {
      const why = decisionReasonText(reason);
      const text = why === outcomeText(outcome) ? why : `${outcomeText(outcome)} — ${why}`;
      lines.push({ text: `${text}: ${counted(value)}`, value });
    }
  }
  return lines.sort((a, b) => b.value - a.value).map((line) => line.text);
}

// --- "Mind árnyékba" / "Mind ki" (7.2 M5) ----------------------------------------

const LEFT_OUT: Record<string, string> = {
  already: "már ebben a módban van",
  unsupported: "van benne nem értett elem: árnyékban sem futhat",
  no_version: "nincs érvényes verziója",
  not_live_in_klaviyo: "a Klaviyóban nem élő",
};

/** Why a switch of them all leaves a flow out, in words; an unknown code as it came. */
export function leftOutText(code: string): string {
  return LEFT_OUT[code] ?? code;
}

export interface SwitchPlan {
  /** The flows that switch, by country, in the list's order. */
  switching: { country: string; flows: FlowModeCandidate[] }[];
  count: number;
  /** The ones left out, with the why (not the ones already in that mode). */
  leftOut: { flow: FlowModeCandidate; why: string }[];
  already: number;
}

/** What the confirmation lists: the flows that will switch, grouped by country,
 * and the ones left out with why. */
export function switchPlan(flows: FlowModeCandidate[]): SwitchPlan {
  const groups = new Map<string, FlowModeCandidate[]>();
  const leftOut: { flow: FlowModeCandidate; why: string }[] = [];
  let already = 0;
  for (const flow of flows) {
    if (flow.left_out === null) {
      const group = groups.get(flow.country) ?? [];
      group.push(flow);
      groups.set(flow.country, group);
    } else if (flow.left_out === "already") {
      already += 1;
    } else {
      leftOut.push({ flow, why: leftOutText(flow.left_out) });
    }
  }
  const switching = [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([country, members]) => ({ country, flows: members }));
  return { switching, count: switching.reduce((total, group) => total + group.flows.length, 0), leftOut, already };
}

/** The action's name: into shadow or out of it, for one country or all. */
export function switchAllLabel(target: FlowMode, country: string): string {
  const where = country ? ` (${country})` : " (minden ország)";
  return (target === "shadow" ? "Mind árnyékba" : "Mind ki az árnyékból") + where;
}
